import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Award, Target, TrendingUp, Users, CheckCircle, Star } from 'lucide-react';

const QualityPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="section-padding">
        <div className="container-custom max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              Política de Calidad
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Compromiso con la
              <span className="text-gradient"> Excelencia</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              En Atomic Corporation, la calidad no es solo un objetivo, 
              es el fundamento de todo lo que hacemos.
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            <section>
              <div className="flex items-center mb-4">
                <Award className="w-6 h-6 text-primary-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">1. Compromiso con la Calidad</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Atomic Corporation se compromete a proporcionar productos y servicios 
                que cumplan y superen las expectativas de nuestros clientes.
              </p>
              <div className="bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Nuestra Filosofía de Calidad:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">Excelencia en cada detalle de nuestro trabajo</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">Mejora continua como forma de vida</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">Satisfacción total del cliente como meta principal</span>
                  </li>
                </ul>
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <Target className="w-6 h-6 text-primary-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">2. Sistema de Gestión de Calidad</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Implementamos y mantenemos un sistema de gestión de calidad basado 
                en estándares internacionales y mejores prácticas.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Normas y Certificaciones</h3>
                  <ul className="space-y-1 text-blue-700 text-sm">
                    <li>• ISO 9001:2015</li>
                    <li>• OHSAS 18001</li>
                    <li>• Certificaciones sectoriales</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="font-semibold text-green-900 mb-2">Principios del Sistema</h3>
                  <ul className="space-y-1 text-green-700 text-sm">
                    <li>• Enfoque al cliente</li>
                    <li>• Liderazgo y compromiso</li>
                    <li>• Toma de decisiones basada en evidencia</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <TrendingUp className="w-6 h-6 text-primary-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">3. Objetivos de Calidad</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Establecemos objetivos medibles y alcanzables para garantizar 
                la mejora continua de nuestros servicios.
              </p>
              <div className="bg-gradient-to-r from-secondary-50 to-accent-50 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Nuestros Objetivos Clave:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-bold">95%</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Satisfacción del Cliente</h4>
                      <p className="text-gray-600 text-sm">Mantener tasa superior al 95%</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center">
                      <span className="text-accent-600 font-bold">10%</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Reducción de No Conformidades</h4>
                      <p className="text-gray-600 text-sm">Disminuir en un 10% anual</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center">
                      <span className="text-secondary-600 font-bold">98%</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Cumplimiento de Plazos</h4>
                      <p className="text-gray-600 text-sm">Entregar en el 98% de los casos</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 font-bold">100%</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Certificaciones Actualizadas</h4>
                      <p className="text-gray-600 text-sm">Mantener todas vigentes</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <CheckCircle className="w-6 h-6 text-primary-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">4. Garantía de Calidad</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Todos nuestros productos y servicios incluyen garantías que respaldan 
                su inversión y tranquilidad.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-primary-50 rounded-lg p-4">
                  <h3 className="font-semibold text-primary-900 mb-2">Garantía de Productos</h3>
                  <ul className="space-y-1 text-primary-700 text-sm">
                    <li>• Garantía del fabricante completa</li>
                    <li>• Repuestos originales certificados</li>
                    <li>• Documentación de calidad incluida</li>
                  </ul>
                </div>
                <div className="bg-accent-50 rounded-lg p-4">
                  <h3 className="font-semibold text-accent-900 mb-2">Garantía de Servicios</h3>
                  <ul className="space-y-1 text-accent-700 text-sm">
                    <li>• Soporte técnico post-venta</li>
                    <li>• Garantía de satisfacción</li>
                    <li>• Seguimiento continuo del servicio</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <Users className="w-6 h-6 text-primary-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">5. Mejora Continua</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Revisamos regularmente nuestros procesos y servicios para identificar 
                oportunidades de mejora e implementar acciones correctivas.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="font-semibold text-yellow-900 mb-3">Nuestro Ciclo de Mejora:</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-white font-bold text-xl">1</span>
                    </div>
                    <h4 className="font-semibold text-yellow-900 mb-1">Identificar</h4>
                    <p className="text-yellow-700 text-sm">Detectar oportunidades de mejora</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-white font-bold text-xl">2</span>
                    </div>
                    <h4 className="font-semibold text-yellow-900 mb-1">Planificar</h4>
                    <p className="text-yellow-700 text-sm">Desarrollar planes de acción</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-white font-bold text-xl">3</span>
                    </div>
                    <h4 className="font-semibold text-yellow-900 mb-1">Implementar</h4>
                    <p className="text-yellow-700 text-sm">Ejecutar las mejoras</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                      <span className="text-white font-bold text-xl">4</span>
                    </div>
                    <h4 className="font-semibold text-yellow-900 mb-1">Evaluar</h4>
                    <p className="text-yellow-700 text-sm">Medir los resultados</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center mb-4">
                <Star className="w-6 h-6 text-primary-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">6. Reconocimientos y Certificaciones</h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4">
                Nuestro compromiso con la calidad ha sido reconocido por 
                diversas organizaciones y certificadoras internacionales.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-blue-900 mb-1">ISO 9001:2015</h3>
                  <p className="text-blue-700 text-sm">Sistema de Gestión de Calidad</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-green-900 mb-1">OHSAS 18001</h3>
                  <p className="text-green-700 text-sm">Seguridad y Salud Ocupacional</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center">
                  <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Star className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-purple-900 mb-1">Certificación Sectorial</h3>
                  <p className="text-purple-700 text-sm">Especializaciones por industria</p>
                </div>
              </div>
            </section>

            <div className="bg-gradient-to-r from-primary-600 to-accent-500 rounded-xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-4">¿Tienes preguntas sobre nuestra calidad?</h3>
              <p className="text-blue-100 mb-6">
                Nuestro departamento de calidad está disponible para atender tus consultas 
                y recibir tus sugerencias.
              </p>
              <a
                href="mailto:ventas@atomcorporations.com"
                className="inline-flex items-center bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Contactar Departamento de Calidad
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default QualityPolicy;